import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  uploadGerberFiles,
  uploadArchiveFile,
  listDriveFolders,
  linkDriveFolder,
  DriveFolderInfo,
  UploadProgress,
} from '@/services/pcbService';
import { logger } from '@/utils/logger';

interface AddGerberDialogProps {
  partNumber: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

// Gerber file extensions (lowercase)
const GERBER_EXTENSIONS = [
  '.gtl', '.gbl', '.gts', '.gbs', '.gto', '.gbo',
  '.gtp', '.gbp', '.gko', '.gm1', '.gm2', '.gm3',
  '.drl', '.xln', '.ger', '.gbr', '.pho'
];

// Archive file extensions (zip, rar)
const ARCHIVE_EXTENSIONS = ['.zip', '.rar'];

function isGerberFile(filename: string): boolean {
  const ext = filename.toLowerCase().slice(filename.lastIndexOf('.'));
  return GERBER_EXTENSIONS.includes(ext);
}

function isArchiveFile(filename: string): boolean {
  const ext = filename.toLowerCase().slice(filename.lastIndexOf('.'));
  return ARCHIVE_EXTENSIONS.includes(ext);
}

function isAcceptableFile(filename: string): boolean {
  return isGerberFile(filename) || isArchiveFile(filename);
}

const AddGerberDialog: React.FC<AddGerberDialogProps> = ({
  partNumber,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation('psi');
  const [activeTab, setActiveTab] = useState<'upload' | 'drive'>('upload');

  // Upload tab state
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Drive tab state
  const [driveFolders, setDriveFolders] = useState<DriveFolderInfo[]>([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<DriveFolderInfo | null>(null);
  const [linking, setLinking] = useState(false);

  // Load Drive folders when Drive tab is active
  useEffect(() => {
    if (isOpen && activeTab === 'drive' && driveFolders.length === 0) {
      loadDriveFolders();
    }
  }, [isOpen, activeTab]);

  const loadDriveFolders = async () => {
    setLoadingFolders(true);
    try {
      const response = await listDriveFolders();
      setDriveFolders(response.folders || []);
    } catch (error) {
      logger.error('Failed to load Drive folders:', error);
      toast.error('Failed to load folders');
    } finally {
      setLoadingFolders(false);
    }
  };

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // File upload handlers
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const items = e.dataTransfer.items;
    const newFiles: File[] = [];

    // Handle dropped items (could be files or folders)
    const processItems = async () => {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === 'file') {
          const entry = item.webkitGetAsEntry?.();
          if (entry) {
            if (entry.isDirectory) {
              // Read directory contents
              const dirReader = (entry as FileSystemDirectoryEntry).createReader();
              await new Promise<void>((resolve) => {
                dirReader.readEntries((entries) => {
                  entries.forEach((fileEntry) => {
                    if (fileEntry.isFile && isGerberFile(fileEntry.name)) {
                      (fileEntry as FileSystemFileEntry).file((file) => {
                        newFiles.push(file);
                      });
                    }
                  });
                  resolve();
                });
              });
            } else if (isAcceptableFile(entry.name)) {
              const file = item.getAsFile();
              if (file) newFiles.push(file);
            }
          } else {
            const file = item.getAsFile();
            if (file && isAcceptableFile(file.name)) {
              newFiles.push(file);
            }
          }
        }
      }

      // Small delay to ensure all async file reads complete
      setTimeout(() => {
        if (newFiles.length > 0) {
          setUploadFiles((prev) => [...prev, ...newFiles]);
        }
      }, 100);
    };

    processItems();
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const acceptableFiles = Array.from(files).filter((f) => isAcceptableFile(f.name));
      setUploadFiles((prev) => [...prev, ...acceptableFiles]);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (index: number) => {
    setUploadFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (uploadFiles.length === 0) return;

    setUploading(true);
    setUploadProgress(null);
    try {
      // Separate archive files from regular Gerber files
      const archiveFiles = uploadFiles.filter((f) => isArchiveFile(f.name));
      const gerberFiles = uploadFiles.filter((f) => isGerberFile(f.name));

      let totalUploaded = 0;
      const allErrors: string[] = [];

      // Progress callback for SSE updates
      const handleProgress = (progress: UploadProgress) => {
        setUploadProgress(progress);
      };

      // Upload archive files (will be extracted on backend with SSE progress)
      for (const archiveFile of archiveFiles) {
        try {
          setUploadProgress({
            type: 'start',
            message: `Processing ${archiveFile.name}...`,
            current: 0,
            total: 0,
            percentage: 0,
          });
          const response = await uploadArchiveFile(partNumber, archiveFile, handleProgress);
          totalUploaded += response.uploaded_files.length;
          if (response.errors && response.errors.length > 0) {
            allErrors.push(...response.errors);
          }
        } catch (error) {
          logger.error(`Archive upload failed for ${archiveFile.name}:`, error);
          allErrors.push(`${archiveFile.name}: ${error instanceof Error ? error.message : 'Upload failed'}`);
        }
      }

      // Upload regular Gerber files
      if (gerberFiles.length > 0) {
        setUploadProgress({
          type: 'progress',
          message: `Uploading ${gerberFiles.length} Gerber files...`,
          current: 0,
          total: gerberFiles.length,
          percentage: 0,
        });
        const response = await uploadGerberFiles(partNumber, gerberFiles);
        totalUploaded += response.uploaded_files.length;
        if (response.errors && response.errors.length > 0) {
          allErrors.push(...response.errors);
        }
      }

      if (totalUploaded > 0) {
        toast.success(t('pcb.messages.uploadSuccess', { count: totalUploaded }));
        onSuccess();
        onClose();
      }

      if (allErrors.length > 0) {
        toast.error(`Some files failed: ${allErrors.slice(0, 3).join(', ')}${allErrors.length > 3 ? '...' : ''}`);
      }
    } catch (error) {
      logger.error('Upload failed:', error);
      toast.error(t('pcb.messages.uploadFailed'));
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  };

  // Drive folder handlers
  const handleSelectFolder = (folder: DriveFolderInfo) => {
    setSelectedFolder(folder);
  };

  const handleLinkFolder = async () => {
    if (!selectedFolder) return;

    setLinking(true);
    try {
      await linkDriveFolder(partNumber, selectedFolder.id);
      toast.success(t('pcb.messages.linkSuccess'));
      onSuccess();
      onClose();
    } catch (error) {
      logger.error('Link failed:', error);
      toast.error(t('pcb.messages.linkFailed'));
    } finally {
      setLinking(false);
    }
  };

  // Calculate new folder name preview
  const getNewFolderName = (folder: DriveFolderInfo): string => {
    if (folder.name.startsWith(partNumber)) {
      return folder.name;
    }
    return `${partNumber} - ${folder.name}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
          <h3 className="text-lg font-semibold dark:text-white">
            {t('pcb.addGerberDialog.title')} - {partNumber}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-1"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b dark:border-gray-700">
          <button
            onClick={() => setActiveTab('upload')}
            className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'upload'
                ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              {t('pcb.addGerberDialog.tabUpload')}
            </div>
          </button>
          <button
            onClick={() => setActiveTab('drive')}
            className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'drive'
                ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M7.71 3.5L1.15 15l4.58 7.5h13.54l4.58-7.5L17.29 3.5H7.71zM6.29 5.5h4.71l-4.5 7H2.29l4-7zm.5 8h4.42l2.29 4H4.79l2-4zm8.92-8h4.71l4 7H19.5l-4.5-7h.71zm-.42 8l-2.29 4-2.29-4h4.58zM12 7l4.5 7h-9L12 7z" />
              </svg>
              {t('pcb.addGerberDialog.tabDrive')}
            </div>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {activeTab === 'upload' && (
            <div className="space-y-4">
              {/* Drop zone */}
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  dragActive
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <svg className="w-12 h-12 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p className="text-gray-600 dark:text-gray-400 mb-2">
                  {t('pcb.addGerberDialog.dragDropText')}
                </p>
                <p className="text-gray-500 dark:text-gray-500 text-sm mb-4">
                  {t('pcb.addGerberDialog.or')}
                </p>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {t('pcb.addGerberDialog.browseFiles')}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={[...GERBER_EXTENSIONS, ...ARCHIVE_EXTENSIONS].join(',')}
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>

              {/* Selected files */}
              {uploadFiles.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('pcb.addGerberDialog.selectedFiles', { count: uploadFiles.length })}
                  </h4>
                  <div className="max-h-40 overflow-auto border dark:border-gray-600 rounded-lg">
                    {uploadFiles.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between px-3 py-2 border-b dark:border-gray-600 last:border-b-0"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          {isArchiveFile(file.name) ? (
                            <svg className="w-4 h-4 text-amber-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                            </svg>
                          )}
                          <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
                            {file.name}
                          </span>
                          {isArchiveFile(file.name) && (
                            <span className="text-xs text-amber-600 dark:text-amber-400 flex-shrink-0">
                              ({t('pcb.addGerberDialog.willExtract')})
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => handleRemoveFile(index)}
                          className="text-red-500 hover:text-red-700 p-1 flex-shrink-0"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {uploadFiles.length === 0 && !uploading && (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                  {t('pcb.addGerberDialog.noFilesSelected')}
                </p>
              )}

              {/* Upload Progress Bar */}
              {uploading && uploadProgress && (
                <div className="space-y-2 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-blue-700 dark:text-blue-300 font-medium">
                      {uploadProgress.message}
                    </span>
                    {uploadProgress.total > 0 && (
                      <span className="text-blue-600 dark:text-blue-400">
                        {uploadProgress.current} / {uploadProgress.total}
                      </span>
                    )}
                  </div>
                  <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-3 overflow-hidden">
                    <div
                      className="bg-blue-600 dark:bg-blue-500 h-3 rounded-full transition-all duration-300 ease-out"
                      style={{ width: `${uploadProgress.percentage}%` }}
                    />
                  </div>
                  {uploadProgress.file_name && (
                    <p className="text-xs text-blue-600 dark:text-blue-400 truncate">
                      {uploadProgress.file_name}
                    </p>
                  )}
                </div>
              )}

              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('pcb.addGerberDialog.supportedFormats')}
              </p>
            </div>
          )}

          {activeTab === 'drive' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('pcb.addGerberDialog.driveDescription')}
              </p>

              {loadingFolders ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-gray-600 dark:text-gray-400">
                    {t('pcb.addGerberDialog.loadingFolders')}
                  </span>
                </div>
              ) : driveFolders.length === 0 ? (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  {t('pcb.addGerberDialog.noFolders')}
                </div>
              ) : (
                <div className="border dark:border-gray-600 rounded-lg max-h-72 overflow-auto">
                  {driveFolders.map((folder) => {
                    const isSelected = selectedFolder?.id === folder.id;
                    const newName = getNewFolderName(folder);
                    const willRename = newName !== folder.name;

                    return (
                      <button
                        key={folder.id}
                        onClick={() => handleSelectFolder(folder)}
                        className={`w-full text-left px-4 py-3 border-b dark:border-gray-600 last:border-b-0 transition-colors ${
                          isSelected
                            ? 'bg-blue-50 dark:bg-blue-900/30 border-l-4 border-l-blue-600'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <svg className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                          </svg>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-700 dark:text-gray-200 truncate">
                              {folder.name}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {t('pcb.addGerberDialog.filesCount', { count: folder.file_count })}
                            </div>
                            {isSelected && willRename && (
                              <div className="mt-2 text-xs text-blue-600 dark:text-blue-400">
                                <span className="font-medium">{t('pcb.addGerberDialog.willRenameTo')}</span>
                                <br />
                                <span className="font-mono">{newName}</span>
                              </div>
                            )}
                          </div>
                          {isSelected && (
                            <span className="px-2 py-1 text-xs bg-blue-600 text-white rounded-full flex-shrink-0">
                              {t('pcb.addGerberDialog.selected')}
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors"
          >
            {t('pcb.addGerberDialog.cancel')}
          </button>

          {activeTab === 'upload' && (
            <button
              onClick={handleUpload}
              disabled={uploading || uploadFiles.length === 0}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {uploading && (
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {uploading ? t('pcb.addGerberDialog.uploading') : t('pcb.addGerberDialog.upload')}
            </button>
          )}

          {activeTab === 'drive' && (
            <button
              onClick={handleLinkFolder}
              disabled={linking || !selectedFolder}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {linking && (
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {linking ? t('pcb.addGerberDialog.linking') : t('pcb.addGerberDialog.link')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddGerberDialog;
